import { useCallback, useEffect, useState } from 'react';
import api from '../services/api';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Fired after a link's icon or colour is saved in Settings > Side Bars, so every side bar already on screen redraws.
export const NAV_STYLES_CHANGED = 'c30:nav-styles-changed';

// A link's identity. Its icon and colour belong to THIS, not to one sector's copy of the link, so a link looks the same wherever it appears (every
// sector, every branch, every person's side bar). The same key is what the server stores the style under.
export const linkStyleKey = (item) => {
  if (!item) return '';
  switch (item.type) {
    case 'builtin_dropdown': return `builtin:${item.builtin_key || ''}`;
    case 'builtin_action': return `action:${item.builtin_key || ''}`;
    case 'page': return `page:${item.route || ''}`;
    case 'custom_link': return `link:${item.url || ''}`;
    case 'custom_dropdown': return `dropdown:${(item.label || '').trim().toLowerCase()}`;
    default: return '';
  }
};

// Transport is always on the side bar (it is not one of a sector's links), so it has an identity of its own: the same as a "Transport" page link.
export const TRANSPORT_LINK = { type: 'page', route: '/transport', label: 'Transport' };

// The classic (non-sector) navigation: the same tabs, so their colours can be set too.
export const CLASSIC_LINKS = [
  { type: 'builtin_dropdown', builtin_key: 'proforma' },
  { type: 'builtin_dropdown', builtin_key: 'quotes' },
  { type: 'builtin_dropdown', builtin_key: 'sales_crm' },
  { type: 'builtin_dropdown', builtin_key: 'stock_transfer_requests' },
  { type: 'page', route: '/po-expenses' },
  { type: 'builtin_dropdown', builtin_key: 'admin' },
];

// The colour a link has until one is chosen for it.
export const DEFAULT_LINK_COLORS = {
  'page:/transport': '#0ea5e9',
  'builtin:proforma': '#e11d48',
  'builtin:quotes': '#ee7203',
  'builtin:sales_crm': '#2563eb',
  'builtin:stock_transfer_requests': '#16a34a',
  'builtin:stock_management': '#b45309',
  'page:/po-expenses': '#c026d3',
  'builtin:admin': '#475569',
};
export const FALLBACK_LINK_COLOR = '#64748b';
// A single link (an item taken out of one of the drop-downs) starts in the colour of the drop-down it came from; the part of its key before the first _ says which.
const ACTION_GROUP_COLORS = { crm: '#2563eb', quotes: '#ee7203', proforma: '#e11d48', stock: '#16a34a', stockctl: '#b45309', admin: '#475569' };
export const defaultColorFor = (item) => {
  if (item && item.type === 'builtin_action') return ACTION_GROUP_COLORS[String(item.builtin_key || '').split('_')[0]] || FALLBACK_LINK_COLOR;
  return DEFAULT_LINK_COLORS[linkStyleKey(item)] || FALLBACK_LINK_COLOR;
};

// Colours offered in Settings > Side Bars (any other colour can be typed or picked too).
export const LINK_COLOR_CHOICES = [
  { name: 'Sky blue', value: '#0ea5e9' }, { name: 'Blue', value: '#2563eb' }, { name: 'Indigo', value: '#4f46e5' }, { name: 'Violet', value: '#9333ea' },
  { name: 'Purple', value: '#7a2182' }, { name: 'Fuchsia', value: '#c026d3' }, { name: 'Rose', value: '#e11d48' }, { name: 'Orange', value: '#ee7203' },
  { name: 'Amber', value: '#f59e0b' }, { name: 'Lime', value: '#cbdd2c' }, { name: 'Green', value: '#16a34a' }, { name: 'Teal', value: '#0d9488' },
  { name: 'Brown', value: '#b45309' }, { name: 'Slate', value: '#475569' }, { name: 'Grey', value: '#64748b' },
];

// The saved icons and colours, fetched once and refreshed whenever one is saved.
export const useNavLinkStyles = () => {
  const [styles, setStyles] = useState({});
  const reload = useCallback(() => {
    api.get(`${API}/nav-header/link-styles`)
      .then((res) => setStyles(res.data?.styles || {}))
      .catch(() => { /* the links keep their default colours */ });
  }, []);
  useEffect(() => {
    reload();
    window.addEventListener(NAV_STYLES_CHANGED, reload);
    return () => window.removeEventListener(NAV_STYLES_CHANGED, reload);
  }, [reload]);
  return { styles, reload };
};

const BUILTIN_TEST_IDS = {
  proforma: ['nav-pill-proforma-dropdown'],
  quotes: ['nav-pill-quotes-dropdown'],
  sales_crm: ['log-call-dropdown-pill', 'log-call-dropdown-pill-disabled'],
  stock_transfer_requests: ['nav-pill-stock-transfers-requests'],
  stock_management: ['nav-pill-stock-management'],
  admin: ['nav-pill-admin-dropdown'],
};

// The test ids the side bar's own buttons carry for a link (the pills are existing components, so this is how a colour finds its button).
export const testIdsFor = (item) => {
  if (!item) return [];
  const safe = (id) => (id !== undefined && id !== null && /^[A-Za-z0-9_-]+$/.test(String(id)) ? String(id) : null);
  switch (item.type) {
    case 'builtin_dropdown': return BUILTIN_TEST_IDS[item.builtin_key] || [];
    case 'builtin_action': return safe(item.builtin_key) ? [`nav-action-${item.builtin_key}`] : [];
    case 'page': {
      const ids = [];
      if (item.route === '/transport') ids.push('v3-nav-transport');
      if (safe(item.id)) ids.push(`nav-header-page-link-${item.id}`);
      return ids;
    }
    case 'custom_link': return safe(item.id) ? [item.url ? `nav-header-custom-link-${item.id}` : `nav-header-page-link-${item.id}`] : [];
    case 'custom_dropdown': return safe(item.id) ? [`nav-header-custom-dropdown-${item.id}`] : [];
    default: return [];
  }
};

// One CSS rule per link that has a chosen colour: it sets the colour the side bar's icon dash is drawn in (--nav-c). Only a strict #rrggbb is ever used.
export const linkColorCss = (items, styles) => {
  const rules = [];
  (items || []).forEach((item) => {
    const key = linkStyleKey(item);
    const color = styles[key]?.color || defaultColorFor(item);
    if (!color || !/^#[0-9a-fA-F]{6}$/.test(color)) return;
    testIdsFor(item).forEach((id) => {
      rules.push(`.v3-bar .v3-actions [data-testid="${id}"] { --nav-c: ${color}; }`);
      // The tab's wrapper carries the colour too, so the slide-out menu (a sibling of the tab button) can use it.
      rules.push(`.v3-bar .v3-actions > div:has(> [data-testid="${id}"]) { --nav-c: ${color}; }`);
    });
  });
  return rules.join('\n');
};

// The rings on the left bar. The page being viewed has an orange ring and stays where it is in the list. When a window is open, its own link (a tab whose
// window is open marks itself data-v3-open; see v3.css, or a window reopened from Recents, see HOST_WINDOW_LINKS) has the orange ring and the page behind the
// window has a purple ring instead. When the bar is minimised the ring is drawn round the icon.
const ringCss = (tail, solid, soft, barExtra = '') => (
  `.v3-bar${barExtra} .v3-actions ${tail} { background: ${soft} !important; border-color: ${solid} !important; box-shadow: inset 0 0 0 1px ${solid} !important; }\n`
  // The open left bar has no pills now (v3.css, Part 54): the link being viewed is shown by a soft wash and a bar down its left edge, in the ring's colour.
  + `.v3-root.v3-root .v3-bar--left:not(.v3-bar--strip)${barExtra} .v3-actions ${tail} { background-color: ${soft} !important; border-color: ${solid} !important; box-shadow: inset 0 0 0 1px ${solid} !important; }\n`
  + `.v3-bar--strip${barExtra} .v3-actions ${tail} { background: transparent !important; box-shadow: none !important; }\n`
  + `.v3-bar--strip${barExtra} .v3-actions ${tail} > svg:first-child { border-color: ${solid}; box-shadow: 0 0 0 2px ${solid}; }`
);
const ORANGE_RING = ['#ee7203', 'rgba(238, 114, 3, .22)'];
const PURPLE_RING = ['var(--v3-purple)', 'var(--v3-purple-soft)'];

// The page being viewed. `item` is a nav item, or a list of test ids. `windowOpen` is true when a window is open that no tab can mark itself (see HOST_WINDOW_LINKS).
export const linkActiveCss = (item, windowOpen = false) => (Array.isArray(item) ? item : testIdsFor(item)).map((id) => {
  const tail = `[data-testid="${id}"]`;
  return [
    ringCss(tail, ...ORANGE_RING),
    ringCss(tail, ...PURPLE_RING, ':has(.v3-actions [data-v3-open="true"])'),
    windowOpen ? ringCss(tail, ...PURPLE_RING) : '',
  ].filter(Boolean).join('\n');
}).join('\n');

// The links of windows that were reopened from Recents / Open (they are drawn by the shell, not by their tab), by the kind of window.
export const HOST_WINDOW_LINKS = {
  proforma: ['nav-pill-proforma-dropdown'],
  quote: ['nav-pill-quotes-dropdown'],
  customer_management: ['log-call-dropdown-pill'],
  project_management: ['log-call-dropdown-pill'],
  add_to_crm: ['log-call-dropdown-pill'],
  add_call_to_crm: ['log-call-dropdown-pill'],
  schedule_call: ['log-call-dropdown-pill'],
  stock_request: ['nav-pill-stock-transfers-requests'],
  b29_transfer: ['nav-pill-stock-transfers-requests'],
};

// The orange ring for those links.
export const linkOpenCss = (ids) => (ids || []).map((id) => ringCss(`[data-testid="${id}"]`, ...ORANGE_RING)).join('\n');
